/* eslint-disable new-cap */
import type { mxGraph } from '@deity-io/mxgraph';
import factory from '@deity-io/mxgraph';
import type { FlowContextInterface } from 'contexts/FlowContext';
import { initLazyZoom } from 'services/mxGraph';
// import mx from 'services/mxGraph/mx';
import { replaceHandleBars } from 'utils/handlebarReplacement';
import { labelBuilder } from '../helpers/mxGraph/labelBuilder';
import {
  getDefaultEdgeStyle,
  getDefaultPortStyle,
  getDefaultVertexStyle,
  getErrorEdgeStyle,
  getGroupVertexStyle,
  getPassTroughPortStyle
} from '../helpers/mxGraph/mx.defaults';
import { menuFactory, modelChangeCB, selectionModelCB } from './callbacks';

/**
 * Find first parent matching the tagName
 * @param child HTMLElement
 * @param tagName string uppercase
 * @param depth int max depth to search
 * @returns HTMLElement|undefined
 */
export const findDOMParent = (child: HTMLElement | undefined, tagName: string, depth = 10): HTMLElement | undefined => {
  if (!child) {
    return;
  }

  if (child.tagName === tagName) {
    return child;
  }
  if (child.parentElement && depth > 0) {
    return findDOMParent(child.parentElement, tagName, depth - 1);
  }
};

const mx = factory({
  mxBasePath: '',
  mxImageBasePath: '/mxGraph/'
});

export const initGraph = (
  // model: any,
  graph: React.MutableRefObject<
    | (mxGraph & {
        lazyZoom?: any;
        mappingData?: any;
        endpointsData?: any;
        modelsData?: any;
      })
    | undefined
  >,
  parent: any,
  editorContainer: any,

  ctx: FlowContextInterface | null,
  isEditable: boolean
) => {
  // model.current = new mx.mxGraphModel();
  graph.current = new mx.mxGraph(editorContainer.current);
  if (!graph.current) {
    return;
  }
  graph.current.zoomFactor = 1.5;
  initLazyZoom(graph.current);

  parent.current = graph.current.getDefaultParent();
  mx.mxEdgeHandler.prototype.snapToTerminals = true;
  mx.mxGraphHandler.prototype.guidesEnabled = true;
  mx.mxConstants.GUIDE_COLOR = 'black';
  // mx.mxConstants.WORD_WRAP = 'break-word';
  mx.mxConstants.VERTEX_SELECTION_STROKEWIDTH = 2;
  mx.mxConstants.VERTEX_SELECTION_DASHED = false;
  mx.mxConstants.VERTEX_SELECTION_COLOR = 'lightgreen';
  mx.mxClient.NO_FO = true;
  // mx.mxClient.imageBasePath = '/images/';

  mx.mxEvent.disableContextMenu(editorContainer.current);
  graph.current.setEnabled(true);
  graph.current.setCellsCloneable(false);
  graph.current.setCellsMovable(isEditable);
  graph.current.setCellsDisconnectable(false);
  graph.current.setAllowDanglingEdges(false);
  graph.current.setCellsEditable(false);
  graph.current.setConnectable(isEditable);
  graph.current.setPanning(true);
  graph.current.setHtmlLabels(true);
  // graph.current.isHtmlLabel = cell => {
  //   return cell.style.indexOf('swimlane') === -1;
  // };
  graph.current.setCellsDeletable(isEditable);
  graph.current.setCellsResizable(isEditable);
  graph.current.setVertexLabelsMovable(false);
  graph.current.setEdgeLabelsMovable(isEditable);

  graph.current.edgeLabelsMovable = isEditable;
  graph.current.resetEdgesOnMove = true;
  graph.current.panningHandler.useLeftButtonForPanning = false;
  graph.current.keepSelectionVisibleOnZoom = false;
  graph.current.popupMenuHandler.autoExpand = true;
  graph.current.popupMenuHandler.factoryMethod = (menu, cell, event) =>
    menuFactory(menu, cell, event, graph, ctx, isEditable);

  graph.current.foldingEnabled = true;

  graph.current.getStylesheet().putDefaultVertexStyle(getDefaultVertexStyle(graph.current));
  graph.current
    .getStylesheet()
    .putDefaultEdgeStyle(getDefaultEdgeStyle(graph.current, mx.mxConstants.EDGESTYLE_ORTHOGONAL));
  graph.current.getStylesheet().putCellStyle('vertex', getDefaultVertexStyle(graph.current));
  graph.current.getStylesheet().putCellStyle('port', getDefaultPortStyle(graph.current));
  graph.current
    .getStylesheet()
    .putCellStyle('connectionError', getErrorEdgeStyle(graph.current, mx.mxConstants.EDGESTYLE_ORTHOGONAL));
  graph.current.getStylesheet().putCellStyle('swimlane', getGroupVertexStyle(graph.current));
  graph.current.getStylesheet().putCellStyle('trigger', getDefaultVertexStyle(graph.current));
  graph.current.getStylesheet().putCellStyle('tail', getDefaultVertexStyle(graph.current));
  graph.current.getStylesheet().putCellStyle('partial', getGroupVertexStyle(graph.current));

  graph.current
    .getStylesheet()
    .putCellStyle('portTop', getDefaultPortStyle(graph.current, mx.mxConstants.DIRECTION_NORTH));
  graph.current
    .getStylesheet()
    .putCellStyle('portBottom', getDefaultPortStyle(graph.current, mx.mxConstants.DIRECTION_SOUTH));
  graph.current
    .getStylesheet()
    .putCellStyle('portLeft', getDefaultPortStyle(graph.current, mx.mxConstants.DIRECTION_WEST));
  graph.current
    .getStylesheet()
    .putCellStyle('portRight', getDefaultPortStyle(graph.current, mx.mxConstants.DIRECTION_EAST));

  graph.current.getStylesheet().putCellStyle('portPassTrough', getPassTroughPortStyle(graph.current));

  graph.current
    .getSelectionModel()
    .addListener(mx.mxEvent.CHANGE, (sender: any, evt: any) => selectionModelCB(sender, evt, ctx));
  // graph.current.connectionHandler.addListener(mx.mxEvent.CONNECT, (sender, evt) => connectionCB(sender, evt, ctx));
  graph.current.getModel().addListener(mx.mxEvent.CHANGE, (sender, evt) => modelChangeCB(sender, evt, ctx));

  /**
   * Add a Listener for the click on the custom toggle-collapse button
   */
  graph.current.addListener(mx.mxEvent.CLICK, (sender, evt) => {
    const src = findDOMParent(evt.properties?.event?.srcElement, 'SPAN', 5);
    if (src && src.classList?.contains('collapse')) {
      // eslint-disable-next-line no-console
      console.log('collapse', evt.properties);
      // const fold = !graph.current!.isCellCollapsed(evt.properties?.cell);
      // graph.current!.foldCells(fold, false, [evt.properties?.cell]);
    }
  });

  /**
   * Setup Vertex/Component render function
   */
  graph.current.convertValueToString = cell => labelBuilder(cell);
  /**
   * Setup Tooltips
   */
  graph.current.setTooltips(true);
  graph.current.getTooltipForCell = cell =>
    replaceHandleBars(cell.value?.detailedDescription || cell.value?.description || '', {
      this: cell.value,
      env: {}
    });

  graph.current.setDropEnabled(isEditable);
  // graph.current.setSplitEnabled(false);

  /**
   * Allow drop on group
   */
  graph.current.isValidDropTarget = (target, cells, evt) => {
    const result = target.isGroup && !target.collapsed && target.isLocked !== true && target.isContentLocked !== true;

    return result;
  };

  /**
   * @todo implement a nice alert override
   */
  // mx.mxUtils.alert = message => {
  //   console.log(message);
  //   alert(message);
  // };

  /**
   * collapsable icon visible
   */
  graph.current.isCellFoldable = (cell, collapse) => cell.isGroup && !cell.isCustomExpand;

  /**
   * Cant use isCellsLocked because i still want the cells to be selectable
   */
  graph.current.isCellMovable = cell => isEditable && cell.parent?.isContentLocked !== true;

  const rubberband = new mx.mxRubberband(graph.current);

  // graph.current.isValidTarget = cell => {
  //   // const style = this.getModel().getStyle(cell);
  //   return true;
  // };
};
